/**
 * @fileoverview Render predicates shared by the components and the screen-entrance plan.
 * @module screen-view-v2/utils/renderPredicates
 *
 * Owner decision D-F14d (F14 fix round 1, 2026-09-28): a screen-entrance slot — and a Carousel slide
 * row — takes a stagger step only if it actually renders. Each predicate below IS the component's
 * own "render nothing" decision, extracted: the component calls it too, so the plan
 * (resolveScreenAnimationPlan) and the render can never disagree. Pure — no React, no DOM.
 */

import type {
  ActionBar as ActionBarType,
  CarouselKind,
  FeaturedMedia,
  HeaderBar as HeaderBarType,
  LocalizedImage,
  LocalizedLabel,
} from '@segmently/schema-models/v2';
import { resolveAssetReference, resolveLabel, type TemplateVariables } from './contentResolver';

/**
 * Text: the label's text for the locale, or null when Text renders nothing (no label, no text
 * for the locale, or an empty one).
 */
export function resolveRenderedLabel(
  label: LocalizedLabel | undefined,
  locale: string,
  variables?: TemplateVariables,
): string | null {
  const content = label ? resolveLabel(label, locale, variables) : null;
  return content === null || content === '' ? null : content;
}

export function labelRenders(label: LocalizedLabel | undefined, locale: string, variables?: TemplateVariables): boolean {
  return resolveRenderedLabel(label, locale, variables) !== null;
}

/**
 * Image: renders only when an asset URL resolves for the locale. Size-independent — the resolver
 * falls back requested → small → original, so any size resolves when one does.
 */
export function imageRenders(image: LocalizedImage | undefined, locale: string): image is LocalizedImage {
  return !!image && resolveAssetReference(image, locale) !== null;
}

/**
 * Media: renders only an Image with its image or a Video with its clip — any other kind, or a
 * missing asset reference, renders nothing (an editor-added media block before an upload).
 */
export function mediaRenders(media: FeaturedMedia | undefined | null): media is FeaturedMedia {
  if (!media) return false;
  const content = media.content as { image?: unknown; video?: unknown } | undefined;
  if (media.kind === 'Image') return Boolean(content?.image);
  if (media.kind === 'Video') return Boolean(content?.video);
  return false;
}

/** HeaderBar's progress indicator: legacy raw numbers, a missing value and the old schema names */
export function normalizeHeaderProgressIndicator(header: HeaderBarType): Record<string, unknown> | undefined {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const raw = (header as any).progressIndicator;
  // Raw number (legacy) → wrap in object
  if (typeof raw === 'number') return { value: raw, kind: 'linearBar' };
  // Object with data
  if (raw && typeof raw === 'object') {
    // Ensure value exists (setNestedValue may have destroyed it)
    if (!('value' in raw)) return { ...raw, value: 50 };
    return raw;
  }
  // Fall back to old schema names (pageIndicator/dashesIndicator)
  const pi = (header as any).pageIndicator;
  const di = (header as any).dashesIndicator;
  const kind = (header as any).pageIndicatorKind;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  if (!pi && !di) return undefined;
  return { ...pi, ...di, ...(kind && { kind }) };
}

/** HeaderBar: renders only with a back / skip / close button or a progress indicator */
export function headerBarRenders(header: HeaderBarType | undefined | null): header is HeaderBarType {
  if (!header) return false;
  const { back, skip, close } = header;
  return Boolean(skip || back || close || normalizeHeaderProgressIndicator(header));
}

/**
 * ActionBar: renders only with a primary or secondary button that is not hidden. The hidden flags
 * are runtime visibility (e.g. hiddenUntilValueEntered); the entrance plan asks without them.
 */
export function actionBarRenders(
  actionBar: ActionBarType | undefined | null,
  primaryHidden = false,
  secondaryHidden = false,
): actionBar is ActionBarType {
  if (!actionBar) return false;
  return Boolean((actionBar.primary && !primaryHidden) || (actionBar.secondary && !secondaryHidden));
}

/** PaywallHeader: renders only with a close button or a restore label */
export function paywallHeaderRenders(closeButton: unknown, restoreLabel: unknown): boolean {
  return Boolean(closeButton || restoreLabel);
}

/** PaywallFooter: renders only with a purchase button, an auto-renew label or legal links */
export function paywallFooterRenders(purchaseAction: unknown, autoRenewLabel: unknown, legalLinks: unknown): boolean {
  return Boolean(purchaseAction || autoRenewLabel || legalLinks);
}

/** SliderItem rows by carousel kind (no kind = every row) */
export const carouselShowsTitle = (kind?: CarouselKind): boolean => !kind || kind !== 'visual';
export const carouselShowsSubtitle = (kind?: CarouselKind): boolean =>
  !kind || kind === 'visualCaptionDetail' || kind === 'visualCaptionDetailExtra';
export const carouselShowsDetail = (kind?: CarouselKind): boolean => !kind || kind === 'visualCaptionDetailExtra';
